import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { normalizePhoneToE164, formatUKForDisplay, isValidUKPhoneNumber } from '@marnic/shared';
import type { Lead } from '@marnic/shared';
import { api } from '../lib/api';
import { extractApiErrorMessage } from '../lib/api-error';
import { softphoneService } from '../softphone/service';
import { useSoftphone } from '../store/softphone';

type LoadState = 'idle' | 'loading' | 'ready' | 'error';

export function CallLauncher(): JSX.Element {
  const [searchParams] = useSearchParams();
  const perfexLeadId = searchParams.get('leadId') ?? '';
  const sourceType = searchParams.get('sourceType') ?? '';
  const sourceId = searchParams.get('sourceId') ?? '';
  const phoneParam = searchParams.get('phone') ?? '';
  const initialPhone = useMemo(() => normalizePhoneToE164(phoneParam) ?? phoneParam, [phoneParam]);

  const [loadState, setLoadState] = useState<LoadState>('idle');
  const [message, setMessage] = useState('');
  const [dialBusy, setDialBusy] = useState(false);

  const lead = useSoftphone((s) => s.currentLead);
  const draftNumber = useSoftphone((s) => s.callDraftNumber);
  const setLead = useSoftphone((s) => s.setLead);
  const setDraftNumber = useSoftphone((s) => s.setCallDraftNumber);
  const selectedCallerId = useSoftphone((s) => s.selectedCallerId);
  const selectedCallerProvider = useSoftphone((s) => s.selectedCallerProvider);

  useEffect(() => {
    if (initialPhone) setDraftNumber(initialPhone);
    if (!perfexLeadId && (!sourceType || !sourceId)) {
      setLoadState(initialPhone ? 'ready' : 'error');
      setMessage(initialPhone ? '' : 'No lead or phone was provided.');
      return;
    }

    const endpoint = sourceType && sourceId
      ? `/lead_context/source/${encodeURIComponent(sourceType)}/${encodeURIComponent(sourceId)}`
      : `/lead_context/${encodeURIComponent(perfexLeadId)}`;
    const qs = initialPhone && !sourceType ? `?phone=${encodeURIComponent(initialPhone)}` : '';
    setLoadState('loading');
    setMessage('');

    void api
      .get<{ lead: Lead }>(`${endpoint}${qs}`)
      .then((data) => {
        setLead(data.lead);
        setDraftNumber(data.lead.phoneE164);
        setLoadState('ready');
      })
      .catch(() => {
        setLoadState(initialPhone ? 'ready' : 'error');
        setMessage(initialPhone ? 'Lead context could not be loaded, but the phone number is ready.' : 'Could not load lead context.');
      });
  }, [initialPhone, perfexLeadId, sourceId, sourceType, setDraftNumber, setLead]);

  const displayName = lead
    ? `${lead.firstName} ${lead.lastName}`.trim()
    : sourceType && sourceId
      ? `${sourceType.replace(/_/g, ' ')} ${sourceId}`
      : perfexLeadId ? `Perfex lead ${perfexLeadId}` : 'Manual call';
  const canDial = !!normalizePhoneToE164(draftNumber) && isValidUKPhoneNumber(draftNumber);

  const callNow = async (): Promise<void> => {
    const e164 = normalizePhoneToE164(draftNumber);
    if (!e164 || !canDial) {
      setMessage('Enter a valid UK number in the softphone.');
      return;
    }

    setDialBusy(true);
    setMessage('');
    try {
      const failureContext = `Could not place call [launcher-debug selected=${selectedCallerId || 'auto'} provider=${selectedCallerProvider ?? 'auto'} to=${e164}]`;
      const result = await softphoneService.dial({
        toRaw: e164,
        leadId: lead?.id,
        callerId: selectedCallerId || undefined,
        callerProvider: selectedCallerProvider ?? undefined,
      });
      if (!result.ok) setMessage(result.detail ?? result.reason ?? failureContext);
    } catch (err) {
      setMessage(extractApiErrorMessage(err, 'Could not place call [launcher-debug]'));
    } finally {
      setDialBusy(false);
    }
  };

  return (
    <div className="mx-auto max-w-2xl space-y-4">
      <header>
        <h1 className="text-2xl font-semibold">Click-to-call</h1>
        <p className="text-sm text-ink-500">Perfex lead launch</p>
      </header>

      <section className="card p-4 sm:p-5">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <div className="text-xs font-medium uppercase text-ink-500">Lead</div>
            <div className="mt-1 text-lg font-semibold">{displayName}</div>
            {lead?.company && <div className="text-sm text-ink-500">{lead.company}</div>}
            <div className="mt-2 font-mono text-sm">{draftNumber ? formatUKForDisplay(draftNumber) : 'No phone'}</div>
          </div>
          <button
            type="button"
            className="btn-primary min-w-32"
            onClick={callNow}
            disabled={!canDial || loadState === 'loading' || dialBusy}
          >
            {dialBusy ? 'Dialling...' : 'Call now'}
          </button>
        </div>

        <div className="mt-4 text-sm">
          {loadState === 'loading' && <span className="text-ink-500">Loading lead context...</span>}
          {loadState === 'ready' && !message && <span className="text-emerald-700">Ready in the softphone.</span>}
          {message && <span className={loadState === 'error' ? 'text-red-700' : 'text-amber-700'}>{message}</span>}
        </div>
      </section>
    </div>
  );
}
